import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Box } from '@mui/material'

// Context providers
import { AuthProvider } from '@/contexts/AuthContext'
import { ChatProvider } from '@/contexts/ChatContext'
import { LanguageProvider } from '@/contexts/LanguageContext'
import { UploadProvider } from '@/contexts/UploadContext'

// Components
import Login from '@/components/auth/Login'
import AcceptInvitation from '@/components/auth/AcceptInvitation'
import ForgotPassword from '@/components/auth/ForgotPassword'
import ResetPassword from '@/components/auth/ResetPassword'
import ChangePassword from '@/components/auth/ChangePassword'
import Dashboard from '@/components/dashboard/Dashboard'
import ProtectedRoute from '@/components/common/ProtectedRoute'
import ProtectedAdminRoute from '@/components/common/ProtectedAdminRoute'
import UploadDashboard from '@/components/upload/UploadDashboard'
import AdminDashboard from '@/components/admin/AdminDashboard'
import Documentation from '@/components/documentation/Documentation'

// Hooks
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from 'react-i18next'

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        {t('app.loading')}
      </Box>
    )
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} 
      />
      <Route 
        path="/accept-invitation" 
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <AcceptInvitation />} 
      />
      <Route 
        path="/forgot-password" 
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPassword />} 
      />
      <Route 
        path="/reset-password" 
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <ResetPassword />} 
      />
      <Route 
        path="/register" 
        element={<Navigate to="/login" replace state={{ message: "Self-registration is disabled. Please contact an administrator for an invitation." }} />} 
      />
      
      {/* Protected routes */}
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <ChatProvider>
              <Dashboard />
            </ChatProvider>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings/change-password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />
      
      {/* Upload feature - ISOLATED, does not affect existing chat */}
      <Route
        path="/upload/*"
        element={
          <ProtectedRoute>
            <UploadProvider>
              <UploadDashboard />
            </UploadProvider>
          </ProtectedRoute>
        }
      />
      
      {/* Documentation */}
      <Route
        path="/documentation"
        element={
          <ProtectedRoute>
            <Documentation />
          </ProtectedRoute>
        }
      />
      
      {/* Admin routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedAdminRoute>
            <AdminDashboard />
          </ProtectedAdminRoute>
        }
      />
      
      {/* Default redirect */}
      <Route
        path="/"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} />
        }
      />
      
      {/* Catch all - redirect to appropriate page */}
      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} />
        }
      />
    </Routes>
  )
}

const App: React.FC = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </Router>
  )
}

export default App
